import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/axios';
import CandidateReportModal from '../../components/custom/CandidateReportModal';
import { formatAppDate } from '../../utils/dateFormat';
import { formatPositionStatusLabel, stackRankingStatusBucket } from '../../utils/positionStatus';
import {
  scoreColor,
  buildSkillScoreBreakdown,
  enrichStackRankingCandidate,
  candidateTotalScore,
  candidateDisplayName,
  mapScoringParametersToSkills,
  DEFAULT_SKILL_WEIGHTAGES,
} from '../../utils/stackRankingScores';

function rankingWizardPath(positionId, preferStep) {
  let step = preferStep || 'skills';
  try {
    const saved = sessionStorage.getItem(`sr_wizard_step_${positionId}`);
    if (!preferStep && saved) {
      if (saved === '3' || saved === 'results') step = 'results';
      else if (saved === '2' || saved === 'upload') step = 'upload';
      else step = 'skills';
    }
  } catch { /* ignore */ }
  return `/stack-ranking/${positionId}?step=${step}`;
}

/* ── colour helpers ── */
const CLIENT_PALETTE = ['#10B981','#6B7280','#F59E0B','#EF4444','#7C3AED','#0041C2','#06B6D4','#EC4899'];
function clientColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return CLIENT_PALETTE[Math.abs(h) % CLIENT_PALETTE.length];
}
function statusCfg(s) {
  if (s === 'Stacked')   return { label: '● Stacked',   bg: 'rgba(16,185,129,.1)',  color: '#059669', border: 'rgba(16,185,129,.2)', dot: '#10B981' };
  if (s === 'Pending') return { label: '● Pending', bg: 'rgba(245,158,11,.1)',  color: '#D97706', border: 'rgba(245,158,11,.2)', dot: '#F59E0B' };
  if (s === 'Closed')    return { label: '● Closed',    bg: 'rgba(156,163,175,.1)', color: '#6B7280', border: 'rgba(156,163,175,.2)', dot: '#9CA3AF' };
  return { label: '● Pending', bg: 'rgba(0,65,194,.08)', color: '#1A5FD4', border: 'rgba(0,65,194,.15)', dot: '#1A5FD4' };
}

/* ── semi-circle gauge ── */
function Gauge({ pct }) {
  const R = 50, cx = 60, cy = 56;
  const sa = Math.PI, ea = 2 * Math.PI;
  const arcLen = (ea - sa) * (pct / 100);
  const sx = cx + R * Math.cos(sa), sy = cy + R * Math.sin(sa);
  const ex = cx + R * Math.cos(sa + arcLen), ey = cy + R * Math.sin(sa + arcLen);
  const trackEx = cx + R * Math.cos(ea), trackEy = cy + R * Math.sin(ea);
  const large = arcLen > Math.PI ? 1 : 0;
  const gc = scoreColor(pct);
  return (
    <div style={{ textAlign: 'center', marginBottom: 16 }}>
      <div style={{ position: 'relative', width: 120, height: 64, margin: '0 auto 6px' }}>
        <svg width="120" height="64" viewBox="0 0 120 64">
          <path d={`M${sx},${sy} A${R},${R} 0 1,1 ${trackEx},${trackEy}`} fill="none" stroke="rgba(0,65,194,.1)" strokeWidth="9" strokeLinecap="round"/>
          {pct > 0 && <path d={`M${sx},${sy} A${R},${R} 0 ${large},1 ${ex},${ey}`} fill="none" stroke={gc} strokeWidth="9" strokeLinecap="round"/>}
        </svg>
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', fontSize: 22, fontWeight: 700, color: gc, lineHeight: 1 }}>{pct}%</div>
      </div>
      <div style={{ fontSize: 10, color: 'var(--ds-muted)', letterSpacing: '.06em', textTransform: 'uppercase' }}>TOP CANDIDATE SCORE</div>
      <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2, color: gc }}>{pct >= 60 ? 'Strong Match' : pct >= 40 ? 'Moderate Match' : 'Low Match'}</div>
    </div>
  );
}

const StackRankingMain = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [positions, setPositions]       = useState([]);
  const [rankings, setRankings]         = useState({});   // positionId → results
  const [loading, setLoading]           = useState(true);
  const [selectedId, setSelectedId]     = useState(() => {
    const focus = Number(searchParams.get('focus'));
    return Number.isFinite(focus) && focus > 0 ? focus : null;
  });
  const autoSelectedRef = useRef(false);
  const [hubLoading, setHubLoading]     = useState(false);
  const [filterQ, setFilterQ]           = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [drawer, setDrawer]           = useState(null); // { type: 'skills'|'candidates', items: [] }

  /* ── fetch all positions + basic ranking status ── */
  const fetchPositions = useCallback(async () => {
    try {
      const res = await api.get('/positions/with-counts');
      const list = res.data || [];
      setPositions(list);

      // batch-check ranking status for positions that have resumes
      const checks = list
        .filter(p => (p.resumeCount ?? 0) > 0)
        .map(async p => {
          try {
            const sr = await api.get(`/stack-ranking/position/${p.positionId}/status`);
            return { id: p.positionId, hasRanking: sr.data?.hasStackRanking === true };
          } catch { return { id: p.positionId, hasRanking: false }; }
        });
      const statuses = await Promise.all(checks);
      const statusMap = {};
      statuses.forEach(s => { statusMap[s.id] = s.hasRanking; });
      setRankings(statusMap);
    } catch (e) {
      console.error('Failed to load positions', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPositions(); }, [fetchPositions]);

  useEffect(() => {
    const refresh = () => { fetchPositions(); };
    window.addEventListener('reachout:position-status-changed', refresh);
    return () => window.removeEventListener('reachout:position-status-changed', refresh);
  }, [fetchPositions]);

  /* ── derive status label per position (Closed beats ranking-complete) ── */
  const posStatus = useCallback((p) => stackRankingStatusBucket(p), []);

  /* ── stat counts ── */
  const stats = useMemo(() => {
    const total = positions.length;
    let stacked = 0, pending = 0, closed = 0;
    positions.forEach(p => {
      const s = posStatus(p);
      if (s === 'Stacked') stacked++;
      else if (s === 'Pending') pending++;
      else if (s === 'Closed') closed++;
    });
    return { total, stacked, pending, closed };
  }, [positions, posStatus]);

  /* ── filtered list ── */
  const filtered = useMemo(() => {
    const q = filterQ.toLowerCase();
    return positions.filter(p => {
      const matchQ = !q ||
        (p.positionName || '').toLowerCase().includes(q) ||
        (p.clientName || '').toLowerCase().includes(q) ||
        String(p.positionId).includes(q);
      const s = posStatus(p);
      const matchS = !filterStatus || s === filterStatus;
      return matchQ && matchS;
    });
  }, [positions, filterQ, filterStatus, posStatus]);

  /* ── selected position & hub data ── */
  const selectedPos = useMemo(() => positions.find(p => p.positionId === selectedId), [positions, selectedId]);
  const [hubResults, setHubResults] = useState(null);
  const [selectedHubCandidate, setSelectedHubCandidate] = useState(null);
  const [hubDetailCandidate, setHubDetailCandidate] = useState(null);

  const selectPosition = useCallback(async (positionId) => {
    setSelectedId(positionId);
    setHubResults(null);
    setSelectedHubCandidate(null);
    setHubDetailCandidate(null);
    const pos = positions.find(p => p.positionId === positionId);
    if (!pos) return;

    setHubLoading(true);
    try {
      let jdId = null;
      try {
        const jds = await api.get(`/job-descriptions/position/${positionId}/versions`);
        const versions = Array.isArray(jds.data) ? jds.data : [];
        if (versions.length > 0) {
          jdId = versions[versions.length - 1].jdId;
        }
      } catch { /* ignore */ }

      const loadResults = async (url) => {
        const r = await api.get(url);
        return (Array.isArray(r.data) ? r.data : []).map(enrichStackRankingCandidate);
      };

      let candidates = await loadResults(`/stack-ranking/position/${positionId}/results`);
      const topFromPosition = candidates.length > 0
        ? Math.max(...candidates.map((c) => candidateTotalScore(c)))
        : 0;

      if (jdId) {
        const fromCand = candidates.find((c) => c.jdId != null)?.jdId;
        if (fromCand != null) jdId = fromCand;
        if (topFromPosition === 0) {
          try {
            const jdCandidates = await loadResults(
              `/stack-ranking/position/${positionId}/jd/${jdId}/results`
            );
            const topJd = jdCandidates.length > 0
              ? Math.max(...jdCandidates.map((c) => candidateTotalScore(c)))
              : 0;
            if (topJd > topFromPosition) {
              candidates = jdCandidates;
            }
          } catch { /* ignore */ }
        }
      }

      candidates.sort((a, b) => candidateTotalScore(b) - candidateTotalScore(a));

      let skills = [];
      const loadSkills = async (id) => {
        if (!id) return [];
        try {
          const sw = await api.get(`/stack-ranking/skill-weightage/jd/${id}`);
          return mapScoringParametersToSkills(sw.data?.scoring_parameters || sw.data?.skills);
        } catch {
          return [];
        }
      };

      if (jdId) {
        skills = await loadSkills(jdId);
      }
      if (skills.length === 0) {
        try {
          const sw = await api.get('/stack-ranking/skill-weightage');
          skills = mapScoringParametersToSkills(sw.data?.scoring_parameters || sw.data?.skills);
        } catch { /* ignore */ }
      }
      if (skills.length === 0) {
        skills = DEFAULT_SKILL_WEIGHTAGES.map((sk) => ({ ...sk }));
      }

      setHubResults({ candidates, skills, jdId });
    } catch { /* no results yet */ }
    finally { setHubLoading(false); }
  }, [positions]);

  /* ── auto-select a position so Score Analysis isn't empty ── */
  useEffect(() => {
    if (!loading && positions.length > 0 && !autoSelectedRef.current) {
      autoSelectedRef.current = true;
      const focus = Number(searchParams.get('focus'));
      const focusExists = Number.isFinite(focus) && positions.some(p => p.positionId === focus);
      const currentExists = selectedId != null && positions.some(p => p.positionId === selectedId);
      selectPosition(focusExists ? focus : (currentExists ? selectedId : positions[0].positionId));
    }
  }, [loading, positions, selectPosition, searchParams, selectedId]);


  /* ── pipeline bar ── */
  const pipelineTotal = Math.max(stats.total, 1);
  const pipelineSegs = [
    { pct: Math.round((stats.stacked / pipelineTotal) * 100), color: '#10B981', label: `Stacked (${stats.stacked})` },
    { pct: Math.round((stats.pending / pipelineTotal) * 100), color: '#F59E0B', label: `Pending (${stats.pending})` },
    { pct: Math.max(0, 100 - Math.round((stats.stacked / pipelineTotal) * 100) - Math.round((stats.pending / pipelineTotal) * 100)), color: '#9CA3AF', label: `Closed (${stats.closed})` },
  ];

  /* ── hub content ── */
  const renderHub = () => {
    if (!selectedPos) return (
      <div style={{ padding: '32px 20px', textAlign: 'center' }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--ds-muted2,#C8D0E0)" strokeWidth="1.4" strokeLinecap="round" style={{ marginBottom: 10 }}>
          <polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>
        </svg>
        <p style={{ fontSize: 12, color: 'var(--ds-muted)', lineHeight: 1.6 }}>Click any position card on the left to instantly see<br/><strong style={{ color: 'var(--ds-text2)' }}>AI score breakdown, top candidates, and quick actions</strong></p>
      </div>
    );

    if (hubLoading) return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ width: 28, height: 28, border: '3px solid var(--ds-border)', borderTop: '3px solid #1A5FD4', borderRadius: '50%', animation: 'srm-spin 0.8s linear infinite', margin: '0 auto 12px' }}/>
        <p style={{ fontSize: 12, color: 'var(--ds-muted)' }}>Loading results…</p>
      </div>
    );

    /* ── candidates from structured hub data (camelCase from /results API) ── */
    const candidates = hubResults?.candidates || [];
    const skills     = hubResults?.skills || [];

    if (!hubResults || candidates.length === 0) return (
      <div>
        <div style={{ padding: '24px 20px 12px', textAlign: 'center' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--ds-muted2,#C8D0E0)" strokeWidth="1.4" strokeLinecap="round" style={{ marginBottom: 8 }}>
            <polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/>
          </svg>
          <p style={{ fontSize: 12, color: 'var(--ds-muted)' }}>No ranking run yet for this position.</p>
        </div>
        <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={() => navigate(`/stack-ranking/${selectedPos.positionId}`)} style={haBtn('grad')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            Run New Ranking
          </button>
          <button onClick={() => navigate(`/stack-ranking/${selectedPos.positionId}`)} style={haBtn()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Upload Resumes
          </button>
        </div>
      </div>
    );

    /* ── score helpers ── */
    const getScore = (c) => candidateTotalScore(c);
    const nCand    = Math.max(candidates.length, 1);
    const avg      = fn => Math.round(candidates.reduce((s, c) => s + Number(fn(c) || 0), 0) / nCand);
    const avgScore = avg(c => c.totalScore ?? c.total_score ?? c.score);

    /* ── active candidate: selected by click, default = top candidate ── */
    const activeCand = selectedHubCandidate || candidates[0];
    const activeScore = activeCand ? getScore(activeCand) : 0;
    const activeName  = activeCand ? candidateDisplayName(activeCand) : '';

    const skillBreakdown = buildSkillScoreBreakdown(activeCand, skills);
    const analysisNote = String(activeCand?.aiAnalysis ?? activeCand?.ai_analysis ?? '');
    const isPlaceholderResult =
      activeScore === 0 &&
      (analysisNote.includes('Not analyzed') || analysisNote.includes('placeholder'));

    const CAND_COLORS = ['#2563EB','#16A34A','#CA8A04','#9333EA','#DC2626','#0EA5E9','#8B5CF6','#F97316'];

    return (
      <div style={{ padding: '16px 16px' }}>
        <Gauge pct={activeScore} />

        {/* candidate name pill */}
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 11, color: 'var(--ds-muted)', marginRight: 4 }}>Viewing:</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ds-text2)' }}>{activeName}</span>
          {selectedHubCandidate && (
            <button
              onClick={() => setSelectedHubCandidate(null)}
              title="Reset to top candidate"
              style={{ marginLeft: 6, fontSize: 10, color: 'var(--ds-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1 }}
            >×</button>
          )}
        </div>

        {/* Two equal-height section boxes */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>

          {/* ── Skill Score Breakdown box ── */}
          <div style={{ border: '1px solid var(--ds-border)', borderRadius: 10, height: 210, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ds-muted)', fontWeight: 600 }}>Skill Score Breakdown</span>
                <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(0,65,194,.1)', color: '#1A5FD4', borderRadius: 10, padding: '1px 6px' }}>{skillBreakdown.length > 0 ? Math.min(skillBreakdown.length, 5) : 0}</span>
              </div>
              {skillBreakdown.length > 5 && (
                <button
                  onClick={() => setDrawer({ type: 'skills', items: skillBreakdown })}
                  style={{ fontSize: 10, color: '#1A5FD4', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, padding: '2px 6px', borderRadius: 6, transition: 'background .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,65,194,.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                >View all ({skillBreakdown.length}) →</button>
              )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: skillBreakdown.length > 0 ? '10px 12px' : '0' }}>
              {skillBreakdown.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ds-muted)', fontSize: 11, textAlign: 'center', padding: '0 8px', lineHeight: 1.5 }}>
                  {isPlaceholderResult
                    ? 'Ranking did not complete for this resume. Open the position and run stack ranking again.'
                    : 'No skill data available'}
                </div>
              ) : skillBreakdown.map((sk, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < skillBreakdown.length - 1 ? 11 : 0 }}>
                  <span style={{ fontSize: 11, color: 'var(--ds-text2)', width: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }} title={sk.name}>{sk.name}</span>
                  <div style={{ flex: 1, height: 5, background: 'rgba(0,65,194,.08)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 3, background: scoreColor(sk.score), width: `${sk.score}%`, transition: 'width .7s ease' }}/>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: scoreColor(sk.score), minWidth: 26, textAlign: 'right' }}>{sk.score}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Top Candidates box ── */}
          <div style={{ border: '1px solid var(--ds-border)', borderRadius: 10, height: 210, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ds-muted)', fontWeight: 600 }}>Top Candidates</span>
                <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(0,65,194,.1)', color: '#1A5FD4', borderRadius: 10, padding: '1px 6px' }}>{Math.min(candidates.length, 5)}</span>
              </div>
              {candidates.length > 5 && (
                <button
                  onClick={() => setDrawer({ type: 'candidates', items: candidates })}
                  style={{ fontSize: 10, color: '#1A5FD4', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, padding: '2px 6px', borderRadius: 6, transition: 'background .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,65,194,.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                >View all ({candidates.length}) →</button>
              )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 4px' }}>
              {candidates.slice(0, 5).map((c, i) => {
                const score    = getScore(c);
                const name     = candidateDisplayName(c);
                const sent     = !!(c.whatsappSent || c.whatsapp_sent);
                const isActive = activeCand === c || (activeCand && (activeCand.resumeId ?? activeCand.resume_id) === (c.resumeId ?? c.resume_id));
                return (
                  <div key={i}
                    onClick={() => setSelectedHubCandidate(c)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderBottom: i < Math.min(candidates.length, 5) - 1 ? '1px solid var(--ds-border)' : 'none', cursor: 'pointer', background: isActive ? 'rgba(0,65,194,.06)' : 'transparent', borderRadius: isActive ? 6 : 0 }}
                    className="srm-tc-row">
                    <div style={{ width: 26, height: 26, borderRadius: 7, background: CAND_COLORS[i % CAND_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                      {(name[0] || '?').toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: isActive ? '#1A5FD4' : 'var(--ds-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                      <div style={{ fontSize: 10, color: 'var(--ds-muted)' }}>Rank #{c.rank ?? i + 1}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: `${scoreColor(score)}18`, color: scoreColor(score), flexShrink: 0 }}>{score}%</span>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: sent ? 'rgba(16,185,129,.15)' : 'rgba(94,106,138,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title={sent ? 'WhatsApp Sent' : 'Not Sent'}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={sent ? '#10B981' : '#8A96B0'} strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.4 1.13 2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                    </div>
                    {/* eye button — opens detail popup */}
                    <button
                      onClick={e => { e.stopPropagation(); setHubDetailCandidate(c); }}
                      title="View candidate details"
                      style={{ width: 20, height: 20, borderRadius: '50%', border: '1px solid rgba(26,95,212,.35)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0 }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#1A5FD4" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={() => navigate(`/stack-ranking/${selectedPos.positionId}`)} style={haBtn('grad')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            Run New Ranking
          </button>
          <button onClick={() => navigate(`/stack-ranking/${selectedPos.positionId}?step=results`)} style={haBtn()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            View Full Results
          </button>
        </div>
      </div>
    );
  };

  /* ── button style helpers ── */
  function haBtn(variant) {
    if (variant === 'grad') return {
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 9,
      fontSize: 12, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer',
      background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', color: '#fff', border: 'none',
      boxShadow: '0 0 12px rgba(0,65,194,.25)', width: '100%', textAlign: 'left'
    };
    return {
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 9,
      fontSize: 12, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer',
      background: 'transparent', color: 'var(--ds-text2)', border: '1px solid var(--ds-border2,var(--ds-border))',
      width: '100%', textAlign: 'left'
    };
  }

  /* ── loading skeleton ── */
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--ds-bg)', fontFamily: "'Plus Jakarta Sans','Inter',sans-serif" }}>
      <style>{`@keyframes srm-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 36, height: 36, border: '3px solid var(--ds-border)', borderTop: '3px solid #1A5FD4', borderRadius: '50%', animation: 'srm-spin 0.8s linear infinite', margin: '0 auto 12px' }}/>
        <p style={{ color: 'var(--ds-muted)', fontSize: 14 }}>Loading stack ranking data…</p>
      </div>
    </div>
  );

  /* ── MAIN RENDER ── */
  return (
    <div style={{ minHeight: '100vh', background: 'var(--ds-bg)', fontFamily: "'Plus Jakarta Sans','Inter',sans-serif", padding: '28px 32px', overflowY: 'auto' }}>
      <style>{`
        @keyframes srm-spin{to{transform:rotate(360deg)}}
        @keyframes srm-slide-in{from{transform:translateX(100%)}to{transform:translateX(0)}}
        .srm-card{background:var(--ds-card);border:1px solid var(--ds-border);border-radius:12px;padding:16px 18px;cursor:pointer;transition:all .2s;display:grid;grid-template-columns:auto 1fr auto;gap:0 14px;align-items:start;}
        .srm-card:hover{border-color:var(--ds-border2,var(--ds-border));box-shadow:var(--ds-shadow-md);transform:translateX(2px);}
        .srm-card.selected{border-color:#0041C2;box-shadow:0 0 0 2px rgba(0,65,194,.12),var(--ds-shadow-md);}
        .srm-ss-card{background:var(--ds-card);border-radius:14px;padding:18px 22px;position:relative;overflow:hidden;cursor:pointer;transition:all .22s;box-shadow:var(--ds-shadow);}
        .srm-ss-card:hover{transform:translateY(-2px);box-shadow:var(--ds-shadow-md);}
        .srm-ss-card.active-stat{border:2px solid #0041C2;box-shadow:0 0 0 3px rgba(0,65,194,.08),var(--ds-shadow-md);}
        .srm-ca-btn{width:28px;height:28px;border-radius:7px;border:1px solid var(--ds-border);background:transparent;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .18s;}
        .srm-ca-btn:hover{border-color:#1A5FD4;background:rgba(0,65,194,.05);}
        .srm-ca-btn.run:hover{border-color:#10B981;background:rgba(16,185,129,.08);}
        .srm-ca-btn.del:hover{border-color:#EF4444;background:rgba(239,68,68,.08);}
        .srm-search:focus-within{border-color:#0041C2;box-shadow:0 0 0 3px rgba(0,65,194,.1);}
        .srm-tc-row:hover{background:var(--ds-hover,rgba(0,65,194,.05));margin:0 -6px;padding:8px 6px;border-radius:6px;}
        .srm-export-btn:hover{border-color:#1A5FD4;color:#1A5FD4;background:rgba(0,65,194,.05);}
        .srm-ha-btn:hover{border-color:#1A5FD4;color:#1A5FD4;background:rgba(0,65,194,.05);}
      `}</style>

      {/* ── HERO ── */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: 'var(--ds-text)', letterSpacing: '-.01em', marginBottom: 4 }}>Stack Ranking</h1>
          <p style={{ fontSize: 13, color: 'var(--ds-muted)' }}>AI-powered resume scoring — click any position to see its score analysis on the right.</p>
        </div>
        <button
          onClick={() => selectedId ? navigate(rankingWizardPath(selectedId)) : navigate('/stack-ranking')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 10, background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', color: '#fff', border: 'none', fontSize: 14, fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer', boxShadow: '0 0 16px rgba(0,65,194,.3)', transition: 'all .2s' }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 36px rgba(0,65,194,.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 16px rgba(0,65,194,.3)'; e.currentTarget.style.transform = 'none'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          Run New Ranking
        </button>
      </div>

      {/* ── STAT STRIP ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 26 }}>
        {[
          { label: 'TOTAL POSITIONS', num: stats.total,   accent: '#0041C2', key: '',         sub: 'Across all clients' },
          { label: 'STACKED',         num: stats.stacked, accent: '#10B981', key: 'Stacked',  sub: 'Ranking complete' },
          { label: 'PENDING',          num: stats.pending, accent: '#F59E0B', key: 'Pending',   sub: 'Awaiting Stack Ranking' },
          { label: 'CLOSED',          num: stats.closed,  accent: '#9CA3AF', key: 'Closed', sub: 'Position filled' },
        ].map((s, i) => (
          <div key={i} className={`srm-ss-card${filterStatus === s.key ? ' active-stat' : ''}`}
            style={{ '--top-color': s.accent, borderTop: `3px solid ${s.accent}` }}
            onClick={() => setFilterStatus(prev => prev === s.key ? '' : s.key)}>
            <div style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ds-muted)', fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: s.accent, flexShrink: 0 }}/>
              {s.label}
            </div>
            <div style={{ fontSize: 36, fontWeight: 700, lineHeight: 1, marginBottom: 6, letterSpacing: '-.02em', color: s.accent }}>{s.num}</div>
            <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── PIPELINE BAR ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>Ranking Pipeline</div>
        <div style={{ height: 10, borderRadius: 10, background: 'var(--ds-border)', overflow: 'hidden', display: 'flex', gap: 2 }}>
          {pipelineSegs.map((seg, i) => (
            <div key={i} style={{ height: '100%', width: `${seg.pct}%`, background: seg.color, borderRadius: i === 0 ? '10px 0 0 10px' : i === pipelineSegs.length - 1 ? '0 10px 10px 0' : 0, transition: 'all .6s ease', minWidth: seg.pct > 0 ? 4 : 0 }}/>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 8, flexWrap: 'wrap' }}>
          {pipelineSegs.map((seg, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ds-muted)', cursor: 'pointer' }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: seg.color, flexShrink: 0 }}/>
              {seg.label}
            </div>
          ))}
        </div>
      </div>

      {/* ── SPLIT VIEW ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'stretch' }}>

        {/* LEFT: position cards */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <div className="srm-search" style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--ds-input-bg,var(--ds-surface))', border: '1px solid var(--ds-border)', borderRadius: 9, padding: '9px 14px', flex: 1, minWidth: 220, transition: 'all .18s' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ds-muted)" strokeWidth="1.6" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input placeholder="Search role, client, or ID…" value={filterQ} onChange={e => setFilterQ(e.target.value)}
                style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 13, fontFamily: 'inherit', color: 'var(--ds-text)', width: '100%' }}/>
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ background: 'var(--ds-input-bg,var(--ds-surface))', border: '1px solid var(--ds-border)', borderRadius: 9, padding: '9px 28px 9px 12px', fontSize: 13, fontFamily: 'inherit', color: 'var(--ds-text)', outline: 'none', cursor: 'pointer', appearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238A96B0' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 9px center' }}>
              <option value="">All Status</option>
              <option value="Stacked">Stacked</option>
              <option value="Pending">Pending</option>
              <option value="Closed">Closed</option>
            </select>
            <button className="srm-export-btn" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, background: 'transparent', border: '1px solid var(--ds-border)', color: 'var(--ds-text2)', fontSize: 13, fontFamily: 'inherit', fontWeight: 500, cursor: 'pointer', transition: 'all .18s', whiteSpace: 'nowrap' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export CSV
            </button>
          </div>

          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 10 }}>
            Showing {filtered.length} position{filtered.length !== 1 ? 's' : ''} · Click a card to analyse
          </div>

          {/* cards list — capped at 5 visible, scrollbar for the rest */}
          <div style={{ maxHeight: 680, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', paddingRight: 4 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ds-muted)', fontSize: 13 }}>No positions match your filters.</div>
            )}
            {filtered.map((p, idx) => {
              const st  = posStatus(p);
              const sc  = statusCfg(st);
              const cc  = clientColor(p.clientName || '');
              const res = p.resumeCount ?? 0;
              const isSelected = selectedId === p.positionId;
              const hubCands  = isSelected ? (hubResults?.candidates || []) : [];
              const top = hubCands.length > 0 ? candidateTotalScore(hubCands[0]) : 0;
              const avgCard = hubCands.length > 0
                ? Math.round(hubCands.reduce((s, c) => s + candidateTotalScore(c), 0) / hubCands.length)
                : 0;

              return (
                <div key={p.positionId} className={`srm-card${selectedId === p.positionId ? ' selected' : ''}`}
                  onClick={() => selectPosition(p.positionId)}>
                  {/* left: sno + icon */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1A5FD4' }}>{idx + 1}</span>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(0,65,194,.08)', border: '1px solid rgba(0,65,194,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1A5FD4" strokeWidth="1.7" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
                    </div>
                  </div>

                  {/* body */}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ds-text)', marginBottom: 3 }}>{p.positionName || 'Untitled'}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--ds-text2)' }}>
                        <div style={{ width: 18, height: 18, borderRadius: 4, background: cc, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{(p.clientName || '?')[0].toUpperCase()}</div>
                        {p.clientName || 'Unknown Client'}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, fontFamily: 'monospace', background: 'var(--ds-surface)', border: '1px solid var(--ds-border)', borderRadius: 5, padding: '1px 7px', color: 'var(--ds-text2)' }}>{p.jobCode || `#${p.positionId}`}</span>
                      <span style={{ fontSize: 11, color: 'var(--ds-muted2,#C8D0E0)' }}>{formatAppDate(p.createdAt || p.openDate || p.openedDate)}</span>
                    </div>
                    {p.createdByName || p.createdBy ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--ds-muted)' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--ds-muted)" strokeWidth="1.7" strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        {(p.createdByName || p.createdBy || '').substring(0, 24)}{(p.createdByName || p.createdBy || '').length > 24 ? '…' : ''}
                      </div>
                    ) : null}

                    {/* score bar / status hint */}
                    {top > 0 ? (
                      <div style={{ marginTop: 6, paddingTop: 8, borderTop: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>Top</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(top), whiteSpace: 'nowrap' }}>{top}%</span>
                        {avgCard > 0 && (
                          <>
                            <span style={{ fontSize: 10, color: 'var(--ds-border2,#C8D0E0)' }}>·</span>
                            <span style={{ fontSize: 10, color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>Avg</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(avgCard), whiteSpace: 'nowrap' }}>{avgCard}%</span>
                          </>
                        )}
                        <div style={{ flex: 1, height: 4, background: 'rgba(0,65,194,.08)', borderRadius: 2, overflow: 'hidden', minWidth: 40 }}>
                          <div style={{ height: '100%', borderRadius: 2, background: scoreColor(top), width: `${top}%`, transition: 'width .6s ease' }}/>
                        </div>
                      </div>
                    ) : st === 'Stacked' ? (
                      <div style={{ marginTop: 6, paddingTop: 8, borderTop: '1px solid var(--ds-border)', fontSize: 10, color: '#10B981', fontStyle: 'italic' }}>
                        AI ranking complete — click to view results
                      </div>
                    ) : st === 'Closed' ? (
                      <div style={{ marginTop: 6, paddingTop: 8, borderTop: '1px solid var(--ds-border)', fontSize: 10, color: '#6B7280', fontStyle: 'italic' }}>
                        Position closed — not counted in active ranking stats
                      </div>
                    ) : (
                      <div style={{ marginTop: 6, paddingTop: 8, borderTop: '1px solid var(--ds-border)', fontSize: 10, color: '#F59E0B', fontStyle: 'italic' }}>
                        Awaiting Stack Ranking — click to start
                      </div>
                    )}
                  </div>

                  {/* right: badge + resume count + actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                      {st === 'Closed' ? `● ${formatPositionStatusLabel(p)}` : sc.label}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--ds-text2)' }}>
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: 'rgba(0,65,194,.1)', color: '#1A5FD4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{res}</div>
                      <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>resumes</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }} onClick={e => e.stopPropagation()}>
                      <button
                        title={st === 'Closed' ? 'Position is closed' : 'Continue this position'}
                        disabled={st === 'Closed'}
                        onClick={() => { if (st !== 'Closed') navigate(rankingWizardPath(p.positionId)); }}
                        style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: st === 'Closed' ? 'rgba(156,163,175,.12)' : 'rgba(16,185,129,.12)', color: st === 'Closed' ? '#9CA3AF' : '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: st === 'Closed' ? 'not-allowed' : 'pointer', transition: 'all .18s', opacity: st === 'Closed' ? 0.55 : 1 }}
                        onMouseEnter={e => { if (st === 'Closed') return; e.currentTarget.style.background = 'rgba(16,185,129,.25)'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                        onMouseLeave={e => { if (st === 'Closed') return; e.currentTarget.style.background = 'rgba(16,185,129,.12)'; e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      </button>
                      <button
                        title="View Details"
                        onClick={() => navigate(`/stack-ranking/${p.positionId}?step=results`)}
                        style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: 'rgba(0,65,194,.1)', color: '#1A5FD4', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all .18s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,65,194,.22)'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,65,194,.1)'; e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* spacer — pushes View all to bottom */}
          <div style={{ flex: 1 }} />

          {/* View all positions — opens sidebar slider */}
          {filtered.length > 5 && (
            <div style={{ paddingTop: 14 }}>
              <button
                onClick={() => setDrawer({ type: 'positions', items: filtered })}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px', borderRadius: 10, border: '1px solid rgba(0,65,194,.2)', background: 'rgba(0,65,194,.05)', color: '#1A5FD4', fontSize: 13, fontFamily: 'inherit', fontWeight: 600, cursor: 'pointer', transition: 'all .18s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,65,194,.12)'; e.currentTarget.style.borderColor = 'rgba(0,65,194,.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,65,194,.05)'; e.currentTarget.style.borderColor = 'rgba(0,65,194,.2)'; }}
              >
                <span>View all {filtered.length} positions</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          )}
        </div>

        {/* RIGHT: Score Analysis Hub */}
        <div style={{ background: 'var(--ds-card)', border: '1px solid var(--ds-border)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--ds-shadow)', display: 'flex', flexDirection: 'column' }}>
          {/* hub header */}
          <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', padding: '18px 20px', color: '#fff', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>Score Analysis</span>
              <span style={{ fontSize: 10, background: selectedPos ? (posStatus(selectedPos) === 'Stacked' ? 'rgba(16,185,129,.3)' : posStatus(selectedPos) === 'Pending' ? 'rgba(245,158,11,.3)' : 'rgba(255,255,255,.2)') : 'rgba(255,255,255,.2)', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                {selectedPos ? posStatus(selectedPos) : 'No selection'}
              </span>
            </div>
            <div style={{ fontSize: 12, opacity: .85, marginTop: 3 }}>
              {selectedPos ? `${selectedPos.positionName} · ${selectedPos.clientName || ''} · ${selectedPos.jobCode || '#' + selectedPos.positionId}` : 'Select a position to see AI scoring details'}
            </div>
          </div>

          {/* hub body */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {renderHub()}
          </div>
        </div>

      </div>

      {/* ── SLIDE-IN DRAWER ── */}
      {drawer && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.35)' }} onClick={() => setDrawer(null)}>
          <div
            style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: 360, background: 'var(--ds-card)', boxShadow: '-6px 0 32px rgba(0,0,0,.18)', borderLeft: '1px solid var(--ds-border)', display: 'flex', flexDirection: 'column', animation: 'srm-slide-in .22s ease', fontFamily: "'Plus Jakarta Sans','Inter',sans-serif" }}
            onClick={e => e.stopPropagation()}
          >
            {/* drawer header */}
            <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, background: 'linear-gradient(135deg,#0041C2,#1A5FD4)' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>
                  {drawer.type === 'skills' ? 'All Skill Parameters' : drawer.type === 'positions' ? 'All Positions' : 'All Candidates'}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.75)', marginTop: 2 }}>
                  {drawer.items.length} {drawer.type === 'skills' ? 'skill dimensions scored' : drawer.type === 'positions' ? 'positions — click to analyse' : 'candidates ranked'}
                </div>
              </div>
              <button
                onClick={() => setDrawer(null)}
                style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,.25)', background: 'rgba(255,255,255,.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, lineHeight: 1, fontFamily: 'inherit', transition: 'background .15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.22)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,.1)'; }}
              >×</button>
            </div>

            {/* drawer body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
              {drawer.type === 'positions' ? drawer.items.map((p, i) => {
                const st = posStatus(p);
                const sc = statusCfg(st);
                const cc = clientColor(p.clientName || '');
                const res = p.resumeCount ?? 0;
                return (
                  <div key={p.positionId}
                    onClick={() => { selectPosition(p.positionId); setDrawer(null); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < drawer.items.length - 1 ? '1px solid var(--ds-border)' : 'none', cursor: 'pointer' }}
                    className="srm-tc-row">
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: cc, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                      {((p.clientName || p.positionName || '?')[0]).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ds-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.positionName || 'Untitled'}</div>
                      <div style={{ fontSize: 10, color: 'var(--ds-muted)' }}>{p.clientName || ''} · {p.jobCode || `#${p.positionId}`}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, whiteSpace: 'nowrap' }}>{sc.label}</span>
                      <span style={{ fontSize: 10, color: 'var(--ds-muted)' }}>{res} resumes</span>
                    </div>
                  </div>
                );
              }) : drawer.type === 'skills' ? drawer.items.map((sk, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < drawer.items.length - 1 ? 14 : 0 }}>
                  <span style={{ fontSize: 11, color: 'var(--ds-text2)', width: 130, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }} title={sk.name}>{sk.name}</span>
                  <div style={{ flex: 1, height: 6, background: 'rgba(0,65,194,.08)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 3, background: scoreColor(sk.score), width: `${sk.score}%`, transition: 'width .7s ease' }}/>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(sk.score), minWidth: 30, textAlign: 'right' }}>{sk.score}%</span>
                </div>
              )) : (() => {
                const DCOLORS = ['#2563EB','#16A34A','#CA8A04','#9333EA','#DC2626','#0EA5E9','#8B5CF6','#F97316'];
                return drawer.items.map((c, i) => {
                  const score = Math.round(Number(c.totalScore ?? c.total_score ?? c.score ?? 0));
                  const name  = c.candidateName || c.name || c.candidate_name || 'Candidate';
                  const sent  = !!(c.whatsappSent || c.whatsapp_sent);
                  return (
                    <div key={i}
                      onClick={() => { setSelectedHubCandidate(c); setDrawer(null); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < drawer.items.length - 1 ? '1px solid var(--ds-border)' : 'none', cursor: 'pointer' }}
                      className="srm-tc-row">
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: DCOLORS[i % DCOLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                        {(name[0] || '?').toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ds-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                        <div style={{ fontSize: 10, color: 'var(--ds-muted)' }}>Rank #{c.rank ?? i + 1}</div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: `${scoreColor(score)}18`, color: scoreColor(score), flexShrink: 0 }}>{score}%</span>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: sent ? 'rgba(16,185,129,.15)' : 'rgba(94,106,138,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title={sent ? 'WhatsApp Sent' : 'Not Sent'}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={sent ? '#10B981' : '#8A96B0'} strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.4 1.13 2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); setHubDetailCandidate(c); setDrawer(null); }}
                        title="View candidate details"
                        style={{ width: 22, height: 22, borderRadius: '50%', border: '1px solid rgba(26,95,212,.35)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, padding: 0 }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#1A5FD4" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      </button>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Candidate Detail Modal ── */}
      {hubDetailCandidate && (
        <CandidateReportModal
          candidate={{
            name: hubDetailCandidate.candidateName || hubDetailCandidate.name || hubDetailCandidate.candidate_name,
            total_score: Math.round(Number(hubDetailCandidate.totalScore ?? hubDetailCandidate.total_score ?? hubDetailCandidate.score ?? 0)),
            rank: hubDetailCandidate.rank,
            email: hubDetailCandidate.email,
            mobile: hubDetailCandidate.mobile,
            experience: hubDetailCandidate.experience,
            company: hubDetailCandidate.company,
            current_position: hubDetailCandidate.current_position || hubDetailCandidate.currentPosition,
            education: hubDetailCandidate.education,
            skills: hubDetailCandidate.skills,
            matching_skills: hubDetailCandidate.matching_skills || hubDetailCandidate.matchingSkills,
            missing_skills: hubDetailCandidate.missing_skills || hubDetailCandidate.missingSkills,
            match_summary: hubDetailCandidate.match_summary || hubDetailCandidate.matchSummary,
            parameter_scores: hubDetailCandidate.parameter_scores || hubDetailCandidate.parameterScores,
            relevant_projects: hubDetailCandidate.relevant_projects || hubDetailCandidate.relevantProjects,
          }}
          skillWeightages={hubResults?.skills || []}
          onClose={() => setHubDetailCandidate(null)}
        />
      )}
      {hubDetailCandidate && (() => {
        const dc = hubDetailCandidate;
        const dcName  = dc.candidateName || dc.name || dc.candidate_name || 'Candidate';
        const dcScore = Math.round(Number(dc.totalScore ?? dc.total_score ?? dc.score ?? 0));
        const sc      = scoreColor(dcScore);
        const row = (label, val) => val && val !== 'Not specified' && val !== 'Not provided' && val !== 'Not analyzed' ? (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ds-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
            <div style={{ fontSize: 13, color: 'var(--ds-text)', lineHeight: 1.5 }}>{val}</div>
          </div>
        ) : null;
        const detailBreakdown = buildSkillScoreBreakdown(dc, hubResults?.skills || []);
        return (
          <div
            onClick={() => setHubDetailCandidate(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          >
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--ds-card)', borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,.18)' }}>
              {/* header */}
              <div style={{ background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', borderRadius: '14px 14px 0 0', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff' }}>
                    {(dcName[0] || '?').toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{dcName}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,.75)' }}>Rank #{dc.rank ?? '—'} · {dc.current_position || dc.currentPosition || 'Not specified'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#fff', padding: '3px 10px', borderRadius: 8, background: `${sc}40` }}>{dcScore}%</span>
                  <button onClick={() => setHubDetailCandidate(null)} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: 7, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 18, lineHeight: 1 }}>×</button>
                </div>
              </div>
              {/* body */}
              <div style={{ overflowY: 'auto', padding: '16px 18px', flex: 1 }}>
                {/* quick info chips */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                  {[
                    { label: 'Email', val: dc.email },
                    { label: 'Phone', val: dc.mobile },
                    { label: 'Experience', val: dc.experience && dc.experience !== 'Not specified' ? `${dc.experience} yrs` : null },
                    { label: 'Company', val: dc.company },
                  ].filter(x => x.val && x.val !== 'Not specified' && x.val !== 'Not provided').map(x => (
                    <div key={x.label} style={{ padding: '4px 10px', borderRadius: 6, background: 'var(--ds-surface)', border: '1px solid var(--ds-border)', fontSize: 11 }}>
                      <span style={{ color: 'var(--ds-muted)', marginRight: 4 }}>{x.label}:</span>
                      <span style={{ fontWeight: 600, color: 'var(--ds-text)' }}>{x.val}</span>
                    </div>
                  ))}
                </div>

                {detailBreakdown.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Skill Score Breakdown</div>
                    {detailBreakdown.map((sk, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < detailBreakdown.length - 1 ? 8 : 0 }}>
                        <span style={{ fontSize: 11, color: 'var(--ds-text2)', width: 130, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }} title={sk.name}>{sk.name}</span>
                        <div style={{ flex: 1, height: 6, background: 'rgba(0,65,194,.08)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 3, background: scoreColor(sk.score), width: `${sk.score}%`, transition: 'width .6s ease' }}/>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(sk.score), minWidth: 30, textAlign: 'right' }}>{sk.score}%</span>
                      </div>
                    ))}
                  </div>
                )}

                {row('Education', dc.education)}
                {row('Skills', dc.skills)}
                {row('Matching Skills', dc.matching_skills || dc.matchingSkills)}
                {row('Missing Skills', dc.missing_skills || dc.missingSkills)}
                {(dc.match_summary || dc.matchSummary) && dc.match_summary !== 'Not specified' && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ds-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '.05em' }}>Match Summary</div>
                    <div style={{ fontSize: 12, color: 'var(--ds-text2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{dc.match_summary || dc.matchSummary}</div>
                  </div>
                )}
              </div>
              {/* footer */}
              <div style={{ padding: '10px 18px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                <button onClick={() => setHubDetailCandidate(null)} style={{ padding: '6px 16px', borderRadius: 7, border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default StackRankingMain;
